// client/src/pages/ShopPOS/components/SplitPaymentModal.tsx
// {esta sección crea el modal para dividir el pago entre efectivo y transferencia}

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";

interface SplitPaymentModalProps {
  show: boolean;
  cartTotal: number;
  getCurrencySymbol: () => string;
  onConfirm: (
    cashAmount: number,
    transferAmount: number,
    customerData: {
      fullName?: string;
      ci?: string;
      phone?: string;
      transferCode?: string;
      bank?: string;
      destinationCard?: string;
      firstFourDigits?: string;
      lastFourDigits?: string;
    },
  ) => void;
  onCancel: () => void;
  bankCards?: any[]; // ← NUEVO: Tarjetas bancarias del shop (igual que TransferFormModal)
}

export function SplitPaymentModal({
  show,
  cartTotal,
  getCurrencySymbol,
  onConfirm,
  onCancel,
  bankCards = [], // ← NUEVO: Valor por defecto empty array
}: SplitPaymentModalProps) {
  const [cashAmount, setCashAmount] = useState("");
  const [transferAmount, setTransferAmount] = useState(0);
  const [error, setError] = useState("");
  // {esta sección agrega estados para recopilar datos del cliente en pago mixto}
  const [customerName, setCustomerName] = useState("");
  const [customerCI, setCustomerCI] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [transferCode, setTransferCode] = useState("");
  const [bank, setBank] = useState("");
  // {esta sección agrega estados para tarjeta destino y dígitos de verificación}
  const [destinationCard, setDestinationCard] = useState("");
  const [firstFourDigits, setFirstFourDigits] = useState("");
  const [lastFourDigits, setLastFourDigits] = useState("");

  // Resetear estado al cerrar/modal cambiar
  useEffect(() => {
    if (!show) {
      setCashAmount("");
      setTransferAmount(0);
      setError("");
      setCustomerName("");
      setCustomerCI("");
      setCustomerPhone("");
      setTransferCode("");
      setBank("");
      setDestinationCard("");
      setFirstFourDigits("");
      setLastFourDigits("");
    }
  }, [show]);

  // {esta sección calcula automáticamente el restante por transferencia}
  const handleCashChange = (value: string) => {
    setCashAmount(value);
    const cash = parseFloat(value) || 0;
    const remaining = cartTotal - cash;

    // Calcular restante con precisión de 2 decimales
    setTransferAmount(Math.max(0, Math.round(remaining * 100) / 100));

    // Validaciones en tiempo real
    if (cash > cartTotal) {
      setError("El efectivo no puede superar el total");
    } else if (cash < 0) {
      setError("El monto debe ser positivo");
    } else if (remaining < 0) {
      setError("El efectivo no puede ser mayor al total");
    } else {
      setError("");
    }
  };

  // {esta sección valida antes de confirmar: ambos montos > 0 y suma === total}
  // {esta sección valida montos y envía los datos del cliente junto con la confirmación}
  const handleConfirm = () => {
    const cash = parseFloat(cashAmount) || 0;
    const transfer = transferAmount;

    // Validación estricta de suma
    const sum = Math.round((cash + transfer) * 100) / 100;
    const totalRounded = Math.round(cartTotal * 100) / 100;

    if (cash <= 0) {
      setError("El monto en efectivo debe ser mayor a 0");
      return;
    }
    if (transfer <= 0) {
      setError("El monto por transferencia debe ser mayor a 0");
      return;
    }
    if (Math.abs(sum - totalRounded) > 0.01) {
      setError(
        `La suma debe ser exactamente ${getCurrencySymbol()}${cartTotal.toFixed(2)}`,
      );
      return;
    }

    // Enviar montos + datos del cliente al padre
    onConfirm(cash, transfer, {
      fullName: customerName.trim(),
      ci: customerCI.trim(),
      phone: customerPhone.trim(),
      transferCode: transferCode.trim(),
      bank: bank.trim(),
      destinationCard: destinationCard.trim(),
      firstFourDigits: firstFourDigits.trim(),
      lastFourDigits: lastFourDigits.trim(),
    });
  };

  if (!show) return null;

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[75] p-4">
      <div className="bg-background rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-auto">
        <h3 className="text-lg font-bold mb-4">Pago Dividido</h3>

        {/* {esta sección muestra el total a pagar} */}
        <div className="text-center mb-4 p-3 bg-muted rounded">
          <div className="text-sm text-muted-foreground">Total a pagar</div>
          <div className="text-3xl font-bold">
            {getCurrencySymbol()}
            {cartTotal.toFixed(2)}
          </div>
        </div>

        {/* {esta sección contiene los inputs de montos} */}
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-1 block">
              Monto en Efectivo
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                {getCurrencySymbol()}
              </span>
              <Input
                type="number"
                placeholder="0.00"
                className="pl-8 text-lg"
                value={cashAmount}
                onChange={(e) => handleCashChange(e.target.value)}
                onFocus={(e) => {
                  if (e.target.value === "0" || e.target.value === "0.00") {
                    e.target.value = "";
                  }
                }}
                min="0.01"
                step="0.01"
                max={cartTotal}
                autoFocus
              />
            </div>
          </div>

          {/* {esta sección muestra el cálculo automático del restante} */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded">
            <div className="text-sm text-blue-800 font-medium">
              Restante por Transferencia
            </div>
            <div className="text-xl font-bold text-blue-900">
              {getCurrencySymbol()}
              {transferAmount.toFixed(2)}
            </div>
          </div>

          {/* {esta sección agrega campos de datos del cliente con validaciones igual que TransferFormModal} */}
          <div className="space-y-3 pt-2 border-t mt-2">
            <label className="text-sm font-medium text-muted-foreground block">
              Datos del Cliente (Transferencia)
            </label>

            <Input
              placeholder="Nombre y Apellidos"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
            />

            <Input
              placeholder="Número de Carnet de Identidad"
              value={customerCI}
              onChange={(e) => {
                // {esta sección valida que el CI solo contenga números}
                const numericValue = e.target.value.replace(/[^0-9]/g, "");
                setCustomerCI(numericValue);
              }}
              inputMode="numeric"
              pattern="[0-9]*"
            />
            {customerCI && customerCI.length < 11 && (
              <p className="text-xs text-amber-600 mt-1">
                El CI debe tener 11 dígitos
              </p>
            )}

            <Input
              placeholder="Número de teléfono (solo números)"
              value={customerPhone}
              onChange={(e) => {
                // {esta sección valida formato de teléfono con soporte para +}
                let value = e.target.value;
                if (value.startsWith("+")) {
                  const numbers = value.substring(1).replace(/[^0-9]/g, "");
                  value = "+" + numbers;
                } else {
                  value = value.replace(/[^0-9]/g, "");
                }
                setCustomerPhone(value);
              }}
              inputMode="tel"
              pattern="[0-9+]*"
            />
            {customerPhone && (
              <p className="text-xs text-gray-500 mt-1">
                Formato: 54882577 o +5354882577
              </p>
            )}

            <Input
              placeholder="Código de transferencia"
              value={transferCode}
              onChange={(e) => setTransferCode(e.target.value)}
            />

            {/* {esta sección agrega selector de banco igual que TransferFormModal} */}
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              value={bank}
              onChange={(e) => setBank(e.target.value)}
            >
              <option value="">Selecciona tu banco</option>
              <option value="Bandec">Bandec</option>
              <option value="BPA">BPA</option>
              <option value="Banco Metropolitano">Banco Metropolitano</option>
            </select>

            {/* {esta sección agrega selector dinámico de tarjeta de destino} */}
            <div className="mt-2">
              <label className="text-sm font-medium text-foreground mb-1 block">
                Tarjeta de Destino (opcional)
              </label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                value={destinationCard || ""}
                onChange={(e) => setDestinationCard(e.target.value)}
              >
                <option value="">Selecciona una tarjeta (opcional)</option>
                {bankCards &&
                  bankCards.map((card) => (
                    <option key={card.id} value={card.cardNumber}>
                      {card.nickname || "Sin nombre"} - {card.cardNumber}
                    </option>
                  ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                {bankCards && bankCards.length > 0
                  ? "Selecciona dónde recibirá el dinero el dueño del negocio"
                  : "No hay tarjetas registradas. Configúralas en Ajustes."}
              </p>
            </div>

            {/* {esta sección agrega inputs para primeros y últimos 4 dígitos con validación numérica} */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">
                  Primeros 4 dígitos (opcional)
                </label>
                <Input
                  placeholder="1234"
                  value={firstFourDigits}
                  onChange={(e) => {
                    // {esta sección valida que solo sean números y máximo 4 dígitos}
                    const numericValue = e.target.value
                      .replace(/[^0-9]/g, "")
                      .slice(0, 4);
                    setFirstFourDigits(numericValue);
                  }}
                  inputMode="numeric"
                  maxLength={4}
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">
                  Últimos 4 dígitos (opcional)
                </label>
                <Input
                  placeholder="5678"
                  value={lastFourDigits}
                  onChange={(e) => {
                    // {esta sección valida que solo sean números y máximo 4 dígitos}
                    const numericValue = e.target.value
                      .replace(/[^0-9]/g, "")
                      .slice(0, 4);
                    setLastFourDigits(numericValue);
                  }}
                  inputMode="numeric"
                  maxLength={4}
                />
              </div>
            </div>
          </div>

          {/* {esta sección muestra errores de validación} */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* {esta sección contiene los botones de acción} */}
          <div className="flex flex-col gap-2 pt-2">
            <Button
              className="w-full"
              onClick={handleConfirm}
              disabled={
                !cashAmount ||
                parseFloat(cashAmount) <= 0 ||
                transferAmount <= 0 ||
                !!error
              }
            >
              Confirmar Pago Dividido
            </Button>
            <Button variant="outline" className="w-full" onClick={onCancel}>
              Volver
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
